import { Icon, Menu, MenuItem, Position } from "@blueprintjs/core"
import CustomButton from "components/Atoms/CustomButton"
import CustomPopover from "components/Atoms/CustomPopover"
import StyledLink from "components/Atoms/StyledLink"
import Error from "components/Common/Error"
import ReactTableHeader from "components/Common/ReactTableHeader"
import TablePagination from "components/Molecules/TablePagination"
import ActionMenu from "components/Organisms/ActionMenu"
import ActionMenuItem from "components/Organisms/ActionMenuItem"
import DataCell, { Subtitle, Title } from "components/Organisms/table/DataCell"
import CustomReactTable from "components/Templates/CustomReactTable"
import dayjs from "dayjs"
import advancedFormat from "dayjs/plugin/advancedFormat"
import relativeTime from "dayjs/plugin/relativeTime"
import { useAllPeriodicTasks } from "graphql/periodic-tasks/periodic-tasks.hooks"
import { useSearchFilter } from "hooks/searchFilterHooks"
import { omit } from "lodash-es"
import qs from "query-string"
import React, { useEffect } from "react"
import { useLocation } from "react-router-dom"
import { mountStoreDevtool } from "simple-zustand-devtools"
import { useFilter } from "store/search-filter/search-filter.reducer"
import { createSelectorStore } from "store/selector.store"
import InterfaceFilter from "./InterfaceFilter"
import ReTriggerDialog from "./ReTriggerDialog"
import TaskLogs from "./TaskLogs"
import TaskSelector from "./TaskSelector"
import TaskSelectorAll from "./TaskSelectorAll"
import CustomCard from "components/Atoms/CustomCard"
dayjs.extend(advancedFormat)
dayjs.extend(relativeTime)

const PAGE_SIZE = 20
// import { useRetailGroups } from "graphql/vendor/vendor.hooks";

export const useTaskSelector = createSelectorStore()
mountStoreDevtool("PeriodicTaskSelector", useTaskSelector)
const Interfaces = () => {
  const setResultSet = useTaskSelector(store => store.setResultSet)
  const [filter, setFilter] = useSearchFilter()
  const [filterState] = useFilter("interfaces")

  const page = parseInt(filter.page)
  const { data, loading, error } = useAllPeriodicTasks({
    first: PAGE_SIZE,
    skip: page * PAGE_SIZE,
    query: filter.search,
    filter: {
      ...omit(filterState, ["startTime"]),
      task: filterState.task?.id,
      isOk: filterState.isOk ?? undefined,
      startTime_Lte:
        filterState.startTime?.[1] &&
        dayjs(filterState.startTime?.[1]).toISOString(),
      startTime_Gte:
        filterState.startTime?.[0] &&
        dayjs(filterState.startTime?.[0]).toISOString(),
    },
  })

  const tasks = data?.allPeriodicTasks

  useEffect(() => {
    if (tasks?.length) {
      setResultSet(tasks)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks])
  // const groups = useRetailGroups();
  if (error) return <Error message={error?.message} />

  const COLUMNS = [
    {
      Header: "#",
      width: 45,
      sortable: false,
      resizable: false,
      className: "text-center",
      Cell: ({ index }) => PAGE_SIZE * ((page || 1) - 1) + index + 1,
    },
    {
      width: 50,
      accessor: "id",
      sortable: false,
      Header() {
        return <TaskSelectorAll />
      },
      Cell({ original }) {
        return <TaskSelector value={original} />
      },
    },
    {
      width: 50,
      Header: "",
      sortable: false,
      Cell({ original }) {
        return (
          <CustomPopover
            position={Position.BOTTOM_LEFT}
            minimal={true}
            content={
              <Menu>
                <MenuItem
                  text="Re-trigger"
                  onClick={() =>
                    setFilter(prev => ({
                      ...prev,
                      task: original.id,
                      view: "trigger",
                      name: original.task,
                    }))
                  }
                />
              </Menu>
            }
          >
            <CustomButton
              style={{ transform: "rotate(90deg)" }}
              icon="more"
              minimal={true}
            />
          </CustomPopover>
        )
      },
    },
    {
      width: 240,
      Header: "Task Name",
      accessor: "task",
      Cell({ value, original }) {
        const location = useLocation()
        return (
          <StyledLink
            to={{
              pathname: location.pathname,
              search: qs.stringify({
                task: original.id,
                search: filter.search,
                view: "logs",
              }),
            }}
          >
            {value}
          </StyledLink>
        )
      },
    },

    {
      Header: "Description",
      accessor: "name",
    },
    {
      width: 130,
      Header: "Request Status",
      accessor: "enabled",
      Cell({ value }) {
        return (
          <div>
            <span className="flex items-center px-3 w-fit">
              <Icon
                className="mr-2"
                icon={value ? "endorsed" : "outdated"}
                intent={value ? "success" : "none"}
              />
              {value ? "Sent" : "Not sent"}
            </span>
          </div>
        )
      },
    },
    {
      width: 130,
      Header: "Response Status",
      accessor: "latestLog.isOk",
      Cell({ value }) {
        return (
          <div>
            <span className="flex items-center px-3 w-fit">
              <Icon
                className="mr-2"
                icon={value ? "endorsed" : "delete"}
                intent={value ? "success" : "danger"}
              />
              {value ? "Pass" : "Fail"}
            </span>
          </div>
        )
      },
    },
    {
      width: 200,
      Header: "Execution Time",
      accessor: "startTime",
      Cell({ value }) {
        return value ? (
          <DataCell>
            <Title>{dayjs(value).fromNow()}</Title>
            <Subtitle>{dayjs(value).format("Do MMM YYYY hh:MM:ss a")}</Subtitle>
          </DataCell>
        ) : (
          "---"
        )
      },
    },
    {
      width: 130,
      Header: "Activity Type",
      accessor: "oneOff",
      Cell({ value }) {
        return (
          <>
            {value ? null : <Icon className="mr-1" icon={"refresh"} />}
            {value ? "One time" : "Continuous"}
          </>
        )
      },
    },
    {
      width: 240,
      Header: "Executed In (Schedule)",
      accessor: "scheduleName",
    },
  ]

  return (
    <>
      <CustomCard className="m-3 p-0">
        <ReactTableHeader
          icon="third-party"
          title={"Interfaces' Tasks"}
          filter={<InterfaceFilter />}
          value={filter?.search}
          onQueryChange={query => {
            setFilter(prev => ({
              ...prev,
              search: query,
            }))
          }}

          // filter={<BenchmarkFilter />}
        >
          <ActionMenu
            className="mr-3"
            onClick={() =>
              setFilter(prev => ({
                ...omit(prev, ["task", "name"]),
                view: "trigger",
              }))
            }
          >
            <ActionMenuItem text="Re-trigger tasks" />
          </ActionMenu>
          <TablePagination
            disabledNext={data?.allPeriodicTasks?.length < PAGE_SIZE}
            page={page}
            onPageChange={page => {
              setFilter(prev => ({
                ...prev,
                page,
              }))
            }}
          />
        </ReactTableHeader>
        <CustomReactTable
          showPaginationBottom={false}
          data={tasks ?? []}
          loading={loading}
          columns={COLUMNS}
        />{" "}
      </CustomCard>

      <TaskLogs
        taskId={filter.task}
        isOpen={parseInt(filter.task) && filter.view === "logs" ? true : false}
        onClose={() => setFilter(prev => omit(prev, ["task", "view"]))}
      />
      <ReTriggerDialog />
    </>
  )
}

export default Interfaces
